import { DateTime } from 'luxon';
import {
  ACTIVITY_COLUMNS,
  ACTIVITY_TYPES,
  AUTO_REPORT_VALUE,
  FREQUENCIES,
  ImportActivityType,
  ImportFrequency,
  INDIVIDUAL_REPORT_OPTIONS,
  isColumnApplicable,
  ONE_OFF_TYPES,
  oneOffKey,
  sessionKey,
  WEEKDAY_CODES,
} from './activity-import-columns';
import { ImportActivityRow, ImportLookups, RowErrors } from './activity-import-types';

const DATE_FORMATS = ['yyyy-MM-dd', 'd/M/yyyy', 'd/M/yy', 'd-M-yyyy', 'd.M.yyyy'];
const TIME_FORMATS = ['H:mm', 'H:mm:ss', 'h:mm a', 'h:mma', 'h a', 'ha'];

const TYPE_ALIASES: Record<string, ImportActivityType> = {
  recurring: 'regular',
  once: 'once-off',
  oneoff: 'once-off',
  'one-off': 'once-off',
  selfdirected: 'self-directed',
};

const BOOL_VALUES: Record<string, boolean> = {
  yes: true,
  y: true,
  true: true,
  1: true,
  no: false,
  n: false,
  false: false,
  0: false,
};

export const normaliseKey = (value: string) => value.trim().toLowerCase();

export const splitList = (value?: string): string[] =>
  (value ?? '')
    .split(/[;\n]/)
    .map((item) => item.trim())
    .filter(Boolean);

export const parseActivityType = (value?: string): ImportActivityType | null => {
  if (!value) return null;
  const key = normaliseKey(value).replace(/[\s_]+/g, '-');
  if ((ACTIVITY_TYPES as string[]).includes(key)) return key as ImportActivityType;
  return TYPE_ALIASES[key] ?? null;
};

export const parseFrequency = (value?: string): ImportFrequency | null => {
  if (!value) return null;
  const key = normaliseKey(value).replace(/\s+/g, '-');
  return (FREQUENCIES as readonly string[]).includes(key) ? (key as ImportFrequency) : null;
};

export const parseDate = (value?: string): DateTime | null => {
  if (!value) return null;
  const trimmed = value.trim();
  const match = DATE_FORMATS.map((format) => DateTime.fromFormat(trimmed, format)).find((date) => date.isValid);
  return match ?? null;
};

export const parseTime = (value?: string): DateTime | null => {
  if (!value) return null;
  const trimmed = value.trim().toUpperCase();
  const match = TIME_FORMATS.map((format) => DateTime.fromFormat(trimmed, format)).find((time) => time.isValid);
  return match ?? null;
};

export const parseBool = (value?: string): boolean | null | undefined => {
  if (!value) return null;
  const parsed = BOOL_VALUES[normaliseKey(value)];
  return parsed === undefined ? undefined : parsed;
};

export const parseInteger = (value?: string): number | null | undefined => {
  if (!value) return null;
  if (!/^\d+$/.test(value.trim())) return undefined;
  return Number(value.trim());
};

export const parseDateList = (value?: string): { dates: DateTime[]; invalid: string[] } => {
  const items = splitList(value);
  const dates: DateTime[] = [];
  const invalid: string[] = [];
  items.forEach((item) => {
    const date = parseDate(item);
    if (date) dates.push(date);
    else invalid.push(item);
  });
  return { dates, invalid };
};

const getSlotNumbers = (values: Record<string, string>, pattern: RegExp): number[] => {
  const numbers = new Set<number>();
  Object.keys(values).forEach((key) => {
    const match = key.match(pattern);
    if (match && values[key]) numbers.add(Number(match[1]));
  });
  return Array.from(numbers).sort((a, b) => a - b);
};

export const getSessionNumbers = (values: Record<string, string>) => getSlotNumbers(values, /^session_(\d+)/);

export const getOneOffNumbers = (values: Record<string, string>) => getSlotNumbers(values, /^session_oneoff_(\d+)/);

const lastOf = (numbers: number[]) => (numbers.length ? numbers[numbers.length - 1] : 0);

export const getRowSessionCount = (row: ImportActivityRow): number =>
  Math.max(row.sessionSlots, lastOf(getSessionNumbers(row.values)), 1);

export const getRowOneOffCount = (row: ImportActivityRow): number =>
  Math.max(row.oneOffSlots, lastOf(getOneOffNumbers(row.values)));

const occursOnSchedule = (values: Record<string, string>, date: DateTime): boolean => {
  const start = parseDate(values.startDate);
  const end = parseDate(values.endDate);
  if (!start || date < start || (end && date > end)) return false;

  const interval = parseInteger(values.repeatInterval) || 1;
  const repeatDays = splitList(values.repeatDays).map((day) => day.toUpperCase().slice(0, 2));
  const weeklyDays = repeatDays.length ? repeatDays : [WEEKDAY_CODES[start.weekday - 1]];
  const onWeeklyDay = weeklyDays.includes(WEEKDAY_CODES[date.weekday - 1]);
  const weeksApart = Math.round(date.startOf('week').diff(start.startOf('week'), 'weeks').weeks);
  const monthsApart = (date.year - start.year) * 12 + date.month - start.month;

  switch (parseFrequency(values.frequency)) {
    case 'daily':
      return Math.round(date.diff(start, 'days').days) % interval === 0;
    case 'weekly':
      return onWeeklyDay && weeksApart % interval === 0;
    case 'fortnightly':
      return onWeeklyDay && weeksApart % 2 === 0;
    case 'weekdays':
      return date.weekday <= 5 && weeksApart % interval === 0;
    case 'weekends':
      return date.weekday > 5 && weeksApart % interval === 0;
    case 'monthly-date':
      return date.day === start.day && monthsApart % interval === 0;
    case 'monthly-nth-day':
      return (
        date.weekday === start.weekday &&
        Math.ceil(date.day / 7) === Math.ceil(start.day / 7) &&
        monthsApart % interval === 0
      );
    case 'yearly':
      return date.month === start.month && date.day === start.day && (date.year - start.year) % interval === 0;
    default:
      return true;
  }
};

const findMissing = (names: string[], lookup: Map<string, string>) =>
  names.filter((name) => !lookup.has(normaliseKey(name)));

const validateSessionFields = (
  values: Record<string, string>,
  key: (suffix: string) => string,
  lookups: ImportLookups
): RowErrors => {
  const errors: RowErrors = {};
  const get = (suffix: string) => values[key(suffix)];

  if (!get('')) errors[key('')] = 'Session name is required';

  const start = parseTime(get('_start'));
  const end = parseTime(get('_end'));
  if (get('_start') && !start) errors[key('_start')] = 'Use HH:mm, e.g. 09:00';
  if (get('_end') && !end) errors[key('_end')] = 'Use HH:mm, e.g. 17:00';
  if (start && end && end <= start) errors[key('_end')] = 'End time must be after start time';

  const min = parseInteger(get('_min'));
  const max = parseInteger(get('_max'));
  if (min === undefined) errors[key('_min')] = 'Must be a whole number';
  if (max === undefined) errors[key('_max')] = 'Must be a whole number';
  if (typeof min === 'number' && typeof max === 'number' && min > max) {
    errors[key('_max')] = 'Max must be at least min';
  }

  const reportType = get('_report_type');
  if (
    reportType &&
    normaliseKey(reportType) !== AUTO_REPORT_VALUE &&
    !lookups.reportTypes.has(normaliseKey(reportType))
  ) {
    errors[key('_report_type')] = `No report type named "${reportType}"`;
  }

  const individualReports = get('_individual_reports');
  if (individualReports && !INDIVIDUAL_REPORT_OPTIONS.includes(normaliseKey(individualReports))) {
    errors[key('_individual_reports')] = 'Use optional or mandatory';
  }

  const missingRoles = findMissing(splitList(get('_required_roles')), lookups.roles);
  if (missingRoles.length) errors[key('_required_roles')] = `Unknown roles: ${missingRoles.join(', ')}`;

  return errors;
};

const validateSessions = (values: Record<string, string>, lookups: ImportLookups): RowErrors => {
  const sessionNumbers = getSessionNumbers(values);
  if (sessionNumbers.length === 0) return { [sessionKey(1, '')]: 'At least one session is required' };

  return sessionNumbers.reduce(
    (acc, n) => ({ ...acc, ...validateSessionFields(values, (suffix) => sessionKey(n, suffix), lookups) }),
    {} as RowErrors
  );
};

const validateOneOffs = (
  values: Record<string, string>,
  activityType: ImportActivityType | null,
  lookups: ImportLookups
): RowErrors => {
  const errors: RowErrors = {};
  const oneOffNumbers = getOneOffNumbers(values);
  if (oneOffNumbers.length === 0 || !activityType) return errors;

  if (!ONE_OFF_TYPES.includes(activityType)) {
    return { [oneOffKey(oneOffNumbers[0], '_date')]: 'One-off sessions only work with regular and event activities' };
  }

  const eventDates = new Set(parseDateList(values.dates).dates.map((date) => date.toISODate()));
  const isActivityDate = (date: DateTime) =>
    activityType === 'event' ? eventDates.has(date.toISODate()) : occursOnSchedule(values, date);

  oneOffNumbers.forEach((n) => {
    const key = (suffix: string) => oneOffKey(n, suffix);
    Object.assign(errors, validateSessionFields(values, key, lookups));

    const { dates, invalid } = parseDateList(values[key('_date')]);
    const offSchedule = dates.filter((date) => !isActivityDate(date));
    if (dates.length === 0 && invalid.length === 0) errors[key('_date')] = 'At least one date is required';
    else if (invalid.length) errors[key('_date')] = `Invalid dates: ${invalid.join(', ')}`;
    else if (new Set(dates.map((date) => date.toISODate())).size !== dates.length) {
      errors[key('_date')] = 'Contains duplicate dates';
    } else if (offSchedule.length) {
      errors[key('_date')] = `Not an activity date: ${offSchedule.map((date) => date.toISODate()).join(', ')}`;
    }
  });

  return errors;
};

export const validateRow = (row: ImportActivityRow, lookups: ImportLookups): RowErrors => {
  const { values } = row;
  const errors: RowErrors = {};

  if (!values.activityName) errors.activityName = 'Name is required';

  const activityType = parseActivityType(values.activityType);
  if (!values.activityType) errors.activityType = 'Type is required';
  else if (!activityType) errors.activityType = `Use one of: ${ACTIVITY_TYPES.join(', ')}`;

  if (!values.program) errors.program = 'Program is required';
  else if (!lookups.programs.has(normaliseKey(values.program))) {
    errors.program = `No program named "${values.program}"`;
  }

  const missingTags = findMissing(splitList(values.tags), lookups.tags);
  if (missingTags.length) errors.tags = `Unknown tags: ${missingTags.join(', ')}`;

  const missingRoles = findMissing(splitList(values.requiredRoles), lookups.roles);
  if (missingRoles.length) errors.requiredRoles = `Unknown roles: ${missingRoles.join(', ')}`;

  ACTIVITY_COLUMNS.forEach((column) => {
    const value = values[column.key];
    if (!value || (activityType && !isColumnApplicable(column, activityType))) return;
    if (column.kind === 'bool' && parseBool(value) === undefined) errors[column.key] = 'Use yes or no';
    if (column.kind === 'number' && parseInteger(value) === undefined) errors[column.key] = 'Must be a whole number';
    if (column.kind === 'date' && !parseDate(value)) errors[column.key] = 'Use YYYY-MM-DD or DD/MM/YYYY';
  });

  if (activityType === 'regular' || activityType === 'once-off') {
    if (!values.startDate) errors.startDate = 'Start date is required';
  }

  if (activityType === 'regular') {
    if (!values.frequency) errors.frequency = 'Frequency is required';
    else if (!parseFrequency(values.frequency)) errors.frequency = `Use one of: ${FREQUENCIES.join(', ')}`;

    const start = parseDate(values.startDate);
    const end = parseDate(values.endDate);
    if (start && end && end < start) errors.endDate = 'End date must be after start date';

    const invalidDays = splitList(values.repeatDays).filter(
      (day) => !WEEKDAY_CODES.includes(day.toUpperCase().slice(0, 2))
    );
    if (invalidDays.length) errors.repeatDays = `Use ${WEEKDAY_CODES.join(', ')}`;
    if (parseInteger(values.repeatInterval) === 0) errors.repeatInterval = 'Must be at least 1';
  }

  if (activityType === 'event' || activityType === 'flexible') {
    const { dates, invalid } = parseDateList(values.dates);
    if (invalid.length) errors.dates = `Invalid dates: ${invalid.join(', ')}`;
    else if (activityType === 'event' && dates.length === 0) errors.dates = 'At least one date is required';
    else {
      const unique = new Set(dates.map((date) => date.toISODate()));
      if (unique.size !== dates.length) errors.dates = 'Contains duplicate dates';
    }
  }

  if (values.address && values.remoteDetails) {
    errors.remoteDetails = 'Use either an address or remote details, not both';
  }
  if (values.address && row.geocodeStatus === 'failed') {
    errors.address = 'Address not found. Click to search for it';
  }

  return { ...errors, ...validateSessions(values, lookups), ...validateOneOffs(values, activityType, lookups) };
};
